'use client';

/**
 * ObeyaTimelineCard — full-bleed RFQ timeline (gantt).
 *
 * Wraps the existing `<TimelineView />` so it paints from the
 * cards-everywhere LayoutEngine on `mode1`. Reads `projectSid` from
 * `useObeyaProjectData` and **always** renders the project-wide
 * view — every project RFQ (and each tender's per-vendor sibling)
 * becomes its own row.
 *
 * Crucially does NOT pass `selectedRfqSid`: the legacy `<ObeyaWidget />`
 * passed it whenever an RFQ was selected in the graph store, which
 * caused `<TimelineView />` to *filter* to a single row (it switches
 * to the `rfi_chat` strategy and shows only that thread). For a
 * preset whose explicit scope is "the project's procurement calendar"
 * that's the wrong semantics — a Timeline preset should always be
 * project-wide. Drilling into a single thread becomes a separate
 * affordance (click-into, or its own preset) when the design earns it.
 *
 * Designed to be placed full-bleed (`x=0, y=0, w=12, h=12`) — the
 * gantt draws its own header / horizontal scroll and assumes it
 * owns the canvas viewport.
 *
 * @see klyde_edge/composition/cards/obeya.py — content_key="obeya_timeline"
 * @see klyde_ipe/components/widgets/rfi-timeline-view.tsx
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { TimelineView } from './rfi-timeline-view';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';

export function ObeyaTimelineCard() {
  // `<TimelineView />` owns its own fetch (`rfi_threads`); the card
  // only needs to thread the projectId through. Reads it directly
  // from the viewport selection rather than going through
  // `useObeyaProjectData` (which would fire an `obeya.detail` request
  // we'd never use — the timeline doesn't consume aggregate data).
  const { projectId } = useViewportSelection();
  const { contentKey } = useCardIdentity();

  // Scene-only: TimelineView owns its own fetch (rfi_threads), so the card
  // registers its presence + projectId without claiming a payload (rule 5).
  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_timeline',
    status: (projectId ? 'loaded' : 'idle') as 'loaded' | 'idle',
    query_key: null,
    text: 'Timeline',
    json: { project_id: projectId ?? null },
  }), [contentKey, projectId]));

  if (!projectId) return <Empty msg="Select a project" />;

  return (
    <div className="h-full w-full overflow-hidden">
      <TimelineView projectSid={projectId} />
    </div>
  );
}

function Empty({ msg }: { msg: string }) {
  return (
    <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
      {msg}
    </div>
  );
}
